import { get } from "lodash";

import {
  canCreateChild,
  isSupply,
  isDemand,
} from "src/util/directory-customerConfig";
import { hasAdminOrPowerRole } from "src/services/auth";

export const isRealTimeAllocationsEnabled = ({
  user,
  workspace,
  allocationProject,
}) => {
  return (
    hasAdminOrPowerRole(user) &&
    !get(workspace, "config.allocation.disableIndividualAllocation") &&
    (!!get(allocationProject, "enableRealtimeAllocation") ||
      !!get(workspace, "config.featureFlags.enableFutureAllocationsUI"))
  );
};

export const canAddNewTeam = (user, groupTypes, team) => {
  return hasAdminOrPowerRole(user) && canCreateChild(groupTypes, team);
};

export const canChangeSupplyGroup = (workspace) =>
  get(workspace, "config.allocation.enableChangeSupplyGroup", false);

/**
 * Returns true if the group type is a supply type (isSupply, isSupplyRoot, or isSupplySource).
 */
export const isSupplyType = (groupType) =>
  !!(
    groupType &&
    (groupType.isSupply || groupType.isSupplyRoot || groupType.isSupplySource)
  );

/**
 * Returns true if supply teams can be modified (add/move/remove) as children.
 * True when all roots have enableModifySupplyTeams !== false, or when any supply type has allowModifySupplyTeamsOverride.
 */
export const canModifySupplyTeams = (groupTypes) => {
  const types = groupTypes || {};
  const rootTypes = Object.values(types).filter((gt) => gt && gt.isRoot);
  if (rootTypes.length === 0) {
    return true;
  }
  if (rootTypes.every((gt) => gt.enableModifySupplyTeams !== false)) {
    return true;
  }
  const hasOverride = Object.values(types).some(
    (gt) => gt && isSupplyType(gt) && gt.allowModifySupplyTeamsOverride === true
  );
  return hasOverride;
};

/**
 * Returns true if a specific type can be modified (add/move/remove). When root allows modify, all supply types
 * are modifiable; when root disables it, only this type is modifiable if it has allowModifySupplyTeamsOverride.
 */
export const canModifySupplyTeamsForType = (groupTypes, typeId) => {
  if (!isSupplyModifyDisabledAtRoot(groupTypes)) {
    return true;
  }
  const typeConfig = groupTypes?.[typeId];
  return !!(typeConfig && typeConfig.allowModifySupplyTeamsOverride === true);
};

/**
 * Returns true when at least one root has enableModifySupplyTeams === false (used to show override toggle).
 */
export const isSupplyModifyDisabledAtRoot = (groupTypes) => {
  const types = groupTypes || {};
  const rootTypes = Object.values(types).filter((gt) => gt && gt.isRoot);
  if (rootTypes.length === 0) {
    return false;
  }
  return rootTypes.some((gt) => gt.enableModifySupplyTeams === false);
};

/**
 * Returns child type ids that are allowed when adding a child (non-supply types plus supply types
 * that can be modified per canModifySupplyTeamsForType). Use for dropdown/options filtering.
 */
export const getModifiableChildTypes = (groupTypes, childTypeIds) => {
  const ids = childTypeIds ?? [];
  return ids.filter((t) => {
    const typeConfig = groupTypes?.[t];
    if (!isSupplyType(typeConfig)) {
      return true;
    }
    return canModifySupplyTeamsForType(groupTypes, t);
  });
};

/**
 * Returns the names of root types that have enableModifySupplyTeams === false.
 * Used for enforcement messages when supply team settings (renamable, can create child) are locked.
 */
export const getRootTypesWithSupplyModifyDisabled = (groupTypes) => {
  const types = groupTypes || {};
  return Object.values(types)
    .filter((gt) => gt && gt.isRoot && gt.enableModifySupplyTeams === false)
    .map((gt) => gt.name || gt.id);
};

export const isAddingMemberEnabled = (workspace, team) => {
  return (
    isDemand(get(workspace, "config.groupTypes"), team) ||
    (canChangeSupplyGroup(workspace) &&
      isSupply(get(workspace, "config.groupTypes"), team))
  );
};
